import { concat, slice } from 'lodash-es';

import {
  V4_END_NODE_ID,
  V4_ID,
  V4_START_NODE_ID,
  V5_END_NODE_ID,
  V5_ID,
  V5_START_NODE_ID,
} from '../../../services/bolt/constants';
import type { Records } from '../../../services/search/types';
import { type Node, type Relationship } from '../../../types/graph';
import { isVirtualRelationshipFactory } from '../../graph/relationships';

interface ResultsByUniqueNodesProps {
  previousRecords: Records;
  newRecords: Records;
  uniqueNodesIds: Set<string>;
  limit: number;
  isV5OrGreater: boolean | null;
  visibleRelationshipTypes: string[];
  relationshipTypes: string[];
}

export const getResultsByUniqueNodes = ({
  previousRecords,
  newRecords,
  uniqueNodesIds,
  limit,
  isV5OrGreater = true,
  visibleRelationshipTypes,
  relationshipTypes,
}: ResultsByUniqueNodesProps) => {
  const totalRecords: Records = { nodes: [], relationships: [] };
  const { nodes, relationships } = newRecords;
  const getNodeByIdKey = (node: Node) => node[isV5OrGreater ? V5_ID : V4_ID];
  const newUniqueNodes = nodes.filter((node: Node) => !uniqueNodesIds.has(getNodeByIdKey(node)));

  const hasNewNodes = newUniqueNodes.length > 0;
  const hasRelationships = relationships.length > 0 || previousRecords.relationships.length > 0; // it should return false if it's not the first iteration and no rels in previous records and no in the current record as well

  const newUniqueNodesIds = new Set(newUniqueNodes.map(getNodeByIdKey));

  const nbrOfPreviousUniqueNodesIds = uniqueNodesIds.size;
  const nbrOfNewUniqueNodesIds = newUniqueNodesIds.size;

  const isUnderLimit = nbrOfPreviousUniqueNodesIds + nbrOfNewUniqueNodesIds < limit;
  const nbrOfAllowedNewUniqueNodesIds = limit - nbrOfPreviousUniqueNodesIds;

  const validNewUniqueNodesIds = isUnderLimit
    ? newUniqueNodesIds
    : new Set(slice(Array.from(newUniqueNodesIds), 0, nbrOfAllowedNewUniqueNodesIds));

  const updatedUniqueNodesIds = new Set([...uniqueNodesIds, ...validNewUniqueNodesIds]);

  const validNewNodes = hasNewNodes
    ? hasRelationships || !isUnderLimit // if there are no relationships, it means we search categories only or single nodes, so every new record is a unique node and no need to filter them by uniqueness, if the limit is reached, we need to filter them in order to keep only the first ones that are under the limit
      ? newUniqueNodes.filter((node: Node) => validNewUniqueNodesIds.has(getNodeByIdKey(node)))
      : newUniqueNodes
    : [];

  const isVirtualRelationship = isVirtualRelationshipFactory(relationshipTypes);

  const isVisibleTypeOrVirtualRelationship = (relationship: Relationship) =>
    visibleRelationshipTypes.includes(relationship.type) || isVirtualRelationship(relationship);

  const validNewRelationships = relationships.filter(
    (rel: Relationship) =>
      updatedUniqueNodesIds.has(rel[isV5OrGreater ? V5_START_NODE_ID : V4_START_NODE_ID]) &&
      updatedUniqueNodesIds.has(rel[isV5OrGreater ? V5_END_NODE_ID : V4_END_NODE_ID]) &&
      isVisibleTypeOrVirtualRelationship(rel),
  );

  totalRecords.nodes = hasNewNodes ? concat(previousRecords.nodes, validNewNodes) : previousRecords.nodes;
  totalRecords.relationships = hasRelationships
    ? concat(previousRecords.relationships, validNewRelationships)
    : previousRecords.relationships;

  return {
    totalRecords,
    uniqueNodesIds: updatedUniqueNodesIds,
    isAboveLimit: !isUnderLimit,
  };
};
